'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _http = require('http');

var _http2 = _interopRequireDefault(_http);

var _https = require('https');

var _https2 = _interopRequireDefault(_https);

var _url = require('url');

var _url2 = _interopRequireDefault(_url);

var _zlib = require('zlib');

var _zlib2 = _interopRequireDefault(_zlib);

var _ConnectionError = require('./ConnectionError');

var _ConnectionError2 = _interopRequireDefault(_ConnectionError);

var _ConsoleLogger = require('./ConsoleLogger');

var _ConsoleLogger2 = _interopRequireDefault(_ConsoleLogger);

var _HTTPError = require('./HTTPError');

var _HTTPError2 = _interopRequireDefault(_HTTPError);

var _ParseError = require('./ParseError');

var _ParseError2 = _interopRequireDefault(_ParseError);

var _StreamReader = require('./StreamReader');

var _StreamReader2 = _interopRequireDefault(_StreamReader);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

const BUILTIN_DEFAULTS = {
  agent: false,
  compression: ['gzip', 'deflate'],
  headers: {},
  json: false,
  logger: new _ConsoleLogger2.default(),
  maxRedirects: 3,
  qs: {},
  resolveWithFullResponse: false,
  timeout: 0,
  verbose: false };
let USER_DEFAULTS = {};

class Request {
  static parseTransport(protocol) {
    switch (protocol) {
      case 'http:':
        return _http2.default;
      case 'https:':
        return _https2.default;
      default:
        throw new TypeError(`New Error: Invalid protocol ${protocol}`);
    }
  }

  static parseQuery(map) {
    if (typeof map !== 'object') {
      throw new TypeError('Invalid query string map');
    }

    const tokens = Object.keys(map).map(key => {
      const unparsedValues = map[key];
      const encodedKey = encodeURIComponent(key);
      let values;

      if (unparsedValues === undefined) {
        return key;
      }

      if (Array.isArray(unparsedValues)) {
        values = unparsedValues.map(encodeURIComponent);
        return values.map(value => `${encodedKey}=${value}`).join('&');
      }

      values = [encodeURIComponent(unparsedValues)];
      return [encodeURIComponent(key), values.join(',')].join('=');
    });

    return tokens.join('&');
  }

  static parseUrl(url, options) {
    url = url || options.url;

    const parsed = _url2.default.parse(url);
    if (options.qs) {
      parsed.search = Request.parseQuery(options.qs);
    }

    const formatted = _url2.default.format(parsed);
    const regex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
    if (!regex.test(formatted)) {
      throw new TypeError(`Invalid URL: ${formatted}`);
    }

    return _url2.default.parse(formatted);
  }

  static parseMethod(method) {
    if (_http2.default.METHODS.indexOf(method) !== -1) {
      return method;
    }

    throw new TypeError(`Invalid method '${method}'`);
  }

  static get defaults() {
    const ENV_DEFAULTS = process.env.RPL_DEFAULTS ? JSON.parse(process.env.RPL_DEFAULTS) : {};

    return Object.assign({}, BUILTIN_DEFAULTS, ENV_DEFAULTS, USER_DEFAULTS);
  }

  static set defaults(defaults) {
    if (typeof defaults !== 'object') {
      const message = `Invalid defaults '${defaults}', expecting an object`;
      throw new TypeError(message);
    }

    USER_DEFAULTS = defaults;
    return Request.defaults;
  }

  constructor(method, url, options) {
    options = options || {};

    this.method = Request.parseMethod(method);
    this.url = Request.parseUrl(url, options);
    this.transport = Request.parseTransport(this.url.protocol);

    this.options = Object.assign({}, Request.defaults, options);
    this.parseOptions();
  }

  parseOptions() {
    const method = this.method;
    const url = this.url;
    const options = this.options;

    const transOpts = {
      headers: options.headers,
      hostname: url.hostname,
      method: method,
      passphrase: options.passphrase,
      path: url.path,
      pfx: options.pfx,
      port: url.port,
      rejectUnauthorized: options.rejectUnauthorized,
      timeout: options.timeout
    };
    let body = options.body;

    if (options.json === true) {
      transOpts.headers.Accept = 'application/json';

      if (body !== undefined) {
        transOpts.headers['Content-Type'] = 'application/json';
        body = JSON.stringify(body);
      }
    }

    if (options.form !== undefined) {
      if (typeof options.form !== 'object') {
        throw new TypeError('Incompatible form data: ', options.form);
      }

      body = Request.parseQuery(options.form);
      transOpts.headers['Content-Type'] = 'application/x-www-form-urlencoded';
      transOpts.headers.Accept = 'application/json';
    }

    if (options.auth !== undefined) {
      if (typeof options.auth !== 'object') {
        throw new TypeError('Incompatible auth data', options.auth);
      }

      const user = options.auth.user || options.auth.username;
      const password = options.auth.pass || options.auth.password;

      transOpts.auth = `${user}:${password}`;
    }

    if (options.compression !== undefined) {
      const comp = options.compression;
      const supported = ['gzip', 'deflate'];

      if (!Array.isArray(comp) || comp.some(v1 => !supported.some(v2 => v1 === v2))) {
        const message = `Invalid compression scheme, '${comp}', expecting string array`;
        throw new TypeError(message);
      }

      transOpts.headers['Accept-Encoding'] = options.compression.join(', ');
    }

    this.transportOptions = transOpts;
    this.body = body;

    this.logger = options.logger;
  }

  run() {
    return this.handleRequest().then(response => this.handleResponse(response));
  }

  createResponse(response, body) {
    if (this.options.json) {
      const str = body.toString();
      if (str.length === 0) {
        body = null;
      } else {
        try {
          body = JSON.parse(str);
        } catch (error) {
          throw new _ParseError2.default(`Invalid JSON: '${body}'`, error.message);
        }
      }
    }

    if (this.options.resolveWithFullResponse) {
      response.body = body;
      return response;
    }

    return body;
  }

  handleResponse(res) {
    const status = res.statusCode;
    const _this = this;

    if (this.options.verbose) {
      this.logger.debug('Response status: %s', res.statusCode);
      this.logger.debug('Response headers: %j', res.headers);
    }

    if (status >= 301 && status <= 303) {
      const location = res.headers.location;
      const options = _this.options;

      if (options.maxRedirects < 1) {
        const message = `Too many redirects to handle ${status}`;
        return Promise.reject(new _ConnectionError2.default(message));
      }

      const newOpts = Object.assign({}, options);
      if (typeof newOpts.qs !== 'undefined') {
        delete newOpts.qs;
      }
      newOpts.maxRedirects = options.maxRedirects - 1;

      const request = new Request(_this.method, location, newOpts);
      return request.handleRequest().then(response => request.handleResponse(response));
    }

    const encoding = res.headers['content-encoding'] || '';
    let readStream;
    switch (encoding) {
      case '':
        readStream = res;
        break;
      case 'gzip':
        readStream = res.pipe(_zlib2.default.createGunzip());
        break;
      case 'deflate':
        readStream = res.pipe(_zlib2.default.createInflate());
        break;
      default:
        return Promise.reject(new _ParseError2.default(`Invalid response encoding: '${encoding}'`));
    }

    const reader = new _StreamReader2.default(readStream);
    return reader.readAll().then(body => {
      if (_this.options.verbose) {
        const decodedBody = body instanceof Buffer ? body.toString() : JSON.stringify(body);
        _this.logger.debug('Response body: %s', decodedBody);
      }

      if (status >= 200 && status < 300) {
        return Promise.resolve(this.createResponse(res, body));
      }

      const response = this.createResponse(res, body);
      const error = new _HTTPError2.default('Error in response', status, response);
      return Promise.reject(error);
    }, error => {
      const message = `Error reading the response: ${error.message}`;
      return Promise.reject(new _ConnectionError2.default(message, error.message));
    });
  }

  handleRequest() {
    const _this = this;

    return new Promise((resolve, reject) => {
      const body = _this.body;

      if (_this.options.verbose) {
        const decodedBody = body instanceof Buffer ? body.toString() : JSON.stringify(body);

        _this.logger.debug('Request URL: %j', _this.url);
        _this.logger.debug('Request headers: %j', _this.transportOptions.headers);
        _this.logger.debug('Request body: %s', decodedBody);
      }

      const transport = _this.transport;
      const transOpts = _this.transportOptions;

      const req = transport.request(transOpts);

      req.on('timeout', () => {
        const message = 'Connection timed out';
        reject(new _ConnectionError2.default(message));
      });
      req.on('abort', () => {
        const rawMessage = 'Client aborted the request';
        const message = `Connection failed: ${rawMessage}`;
        reject(new _ConnectionError2.default(message, rawMessage));
      });
      req.on('aborted', () => {
        const rawMessage = 'Server aborted the request';
        const message = `Connection failed: ${rawMessage}`;
        reject(new _ConnectionError2.default(message, rawMessage));
      });
      req.on('error', error => {
        const message = `Connection failed: ${error.message}`;
        reject(new _ConnectionError2.default(message, error.message));
      });
      req.on('response', response => {
        resolve(response);
      });
      req.end(this.body);
    });
  }
}
exports.default = Request;
module.exports = exports['default'];
//# sourceMappingURL=Request.js.map



// WEBPACK FOOTER //
// ./src/request-promise-lite/lib/Request.js